import parsePhoneNumber from 'libphonenumber-js';
import { DateTime } from 'luxon';

import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';

import { NgIconComponent } from '@ng-icons/core';

import { CapsLockComponent } from '@components/caps-lock/caps-lock.component';
import { ErrorSmallComponent } from '@components/error-handling/error-small/error-small.component';

import { COUNTRIES_KEY_VALUE, FORM_ERRORS } from '@cxc/core/consts';
import { COUNTRIES_ARRAY } from '@cxc/core/consts';

import { API } from '@models/API/index';

import { PreventAutoFillDirective } from '@pages/welcome/signup/prevent-auto-fill.directive';

import { ErrorStore, UserStore } from '@cxc/core/services';
import { IdentityAuthAPI } from '@services/API/identity/auth.service';
import { IdentityInvitationsAPI } from '@services/API/identity/invitations.service';
import { FormService } from '@services/form.service';
import { NavigationService } from '@services/navigation.service';

import { CustomValidators } from '@cxc/core/utils';

const MIN_SIGNUP_AGE = 18;
const MAX_SIGNUP_AGE = 120;

// Why the invitation link cannot be used to sign up.
type InvitationIssue = 'invalid' | 'used';

@Component({
  selector: 'signup-page',
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,

    ButtonModule,
    InputTextModule,
    CheckboxModule,
    RippleModule,
    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    PasswordModule,
    DividerModule,
    DatePicker,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    SelectModule,

    ErrorSmallComponent,
    CapsLockComponent,

    NgIconComponent,
    PreventAutoFillDirective,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class SignupPage implements OnInit {
  // Expose to HTML
  Countries = COUNTRIES_ARRAY;

  // Bounds for the birthdate picker: no future dates, no under-18, no unreasonably old dates
  minBirthdate = DateTime.now().minus({ years: MAX_SIGNUP_AGE }).toJSDate();
  maxBirthdate = DateTime.now().minus({ years: MIN_SIGNUP_AGE }).toJSDate();

  // Plain variables
  goToLoginLabel = $localize`Go to login`;
  invitationCode: string | null = '';
  signUpForm!: FormGroup;

  // angular Core
  navigationService = inject(NavigationService);
  fb = inject(FormBuilder);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);

  // Service utilities
  formService = inject(FormService);
  errorStore = inject(ErrorStore);

  // APIs
  identityAuthAPI = inject(IdentityAuthAPI);
  identityInvitationAPI = inject(IdentityInvitationsAPI);

  // Stores
  userStore = inject(UserStore);

  // Signals
  passwordStrength = signal(0);
  invitationIssue = signal<InvitationIssue | null>(null);

  // Computed
  passwordStrengthPercentile = computed(() => {
    return this.passwordStrength() * (100 / 13);
  });
  passwordTextAndColor = computed(() => {
    if (this.passwordStrength() < 6) {
      return { color: 'var(--p-red-500)', text: $localize`Weak` };
    } else if (this.passwordStrength() < 13) {
      return { color: 'var(--p-yellow-500)', text: $localize`Medium` };
    } else {
      return { color: 'var(--p-green-500)', text: $localize`Strong` };
    }
  });

  constructor() {
    this.invitationCode =
      this.activatedRoute.snapshot.queryParamMap.get('code');

    if (this.invitationCode) {
      this.validateInvitation(this.invitationCode);
    } else {
      this.showInvalidInvitation();
    }

    this.signUpForm = this.fb.group({
      name: [
        '',
        [
          CustomValidators.required(),
          CustomValidators.minLength(4, 'name'),
          CustomValidators.maxLength(50, 'name'),
          CustomValidators.pattern(
            new RegExp(/^[a-zA-Z\xC0-\xFF ]*$/),
            FORM_ERRORS.pattern.onlyLatinLetters('name')
          ),
        ],
      ],
      lastName: [
        '',
        [
          CustomValidators.required(),
          CustomValidators.minLength(4, 'last name'),
          CustomValidators.maxLength(50, 'last name'),
          CustomValidators.pattern(
            new RegExp(/^[a-zA-Z\xC0-\xFF ]*$/),
            FORM_ERRORS.pattern.onlyLatinLetters('last name')
          ),
        ],
      ],
      birthdate: [
        '',
        [
          CustomValidators.required(),
          CustomValidators.minAge(MIN_SIGNUP_AGE, MAX_SIGNUP_AGE),
        ],
      ],
      phoneCountry: [
        COUNTRIES_KEY_VALUE['mexico'],
        [CustomValidators.required()],
      ],
      phone: [
        undefined,
        [
          CustomValidators.required(),
          CustomValidators.maxLength(15, 'phone'),
          CustomValidators.phoneNumber('phoneCountry'),
        ],
      ],
      password: [
        '',
        [CustomValidators.required(), ...CustomValidators.password('password')],
      ],
      confirmPassword: [
        '',
        [
          CustomValidators.required(),
          CustomValidators.matchField('password', 'password'),
        ],
      ],
      conditionsCheckbox: [
        false,
        [CustomValidators.requiredTrue('terms & conditions')],
      ],
    });

    this.signUpForm.valueChanges.subscribe(() => {
      let strength = 0;
      if (this.signUpForm.value['password'].length > 8) {
        strength = 8;
      } else {
        strength = this.signUpForm.value['password'].length;
      }

      strength += /[a-z]/.test(this.signUpForm.value['password']) ? 1 : 0;
      strength += /[A-Z]/.test(this.signUpForm.value['password']) ? 1 : 0;
      strength += /[0-9]/.test(this.signUpForm.value['password']) ? 1 : 0;
      // Checks for allowed symbols
      strength += /[$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]/.test(
        this.signUpForm.value['password']
      )
        ? 1
        : 0;
      // Final full check
      strength +=
        /^(?!\s+)(?!.*\s+$)(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ])[A-Za-z0-9$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]{8,256}$/.test(
          this.signUpForm.value['password']
        )
          ? 1
          : 0;

      this.passwordStrength.set(strength);
    });
  }

  ngOnInit(): void {
    this.errorStore.clear();
  }

  // A resent invitation invalidates the previous code: identity answers 400
  // {"is_valid": false}. Show a dedicated state instead of the form.
  private validateInvitation(invitationCode: string) {
    this.identityInvitationAPI
      .validateInvitationCode(invitationCode)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: response => {
          if (!response.is_valid) {
            this.showInvalidInvitation();
          }
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === HttpStatusCode.BadRequest) {
            this.showInvalidInvitation();
          }
        },
      });
  }

  private showInvalidInvitation(issue: InvitationIssue = 'invalid') {
    // The error interceptor stores the raw response; it must not be shown.
    this.errorStore.clear();
    this.invitationIssue.set(issue);
  }

  signUp() {
    if (this.formService.trySubmitForm(this.signUpForm)) {
      return;
    }

    this.errorStore.clear();

    const phone =
      this.signUpForm.value['phoneCountry'].phoneCode +
      this.signUpForm.value['phone'];

    const user: API.Identity.Auth.Signup.Payload = {
      first_name: this.signUpForm.value['name'],
      last_name: this.signUpForm.value['lastName'],
      invitation_code: this.invitationCode!,
      phone: parsePhoneNumber(phone)!.format('E.164'),
      password: this.signUpForm.value['password'],
      birthdate: DateTime.fromJSDate(
        this.signUpForm.value['birthdate']
      ).toFormat('yyyy-MM-dd'),
      timezone: 'UTC',
    };

    this.identityAuthAPI.register(user).subscribe({
      next: response => {
        this.userStore.patch({
          email: response.email,
        });

        this.navigationService.forward('/login');
      },
      error: (error: HttpErrorResponse) => {
        // The code was invalidated after the page loaded (invitation resent).
        if (error.status === HttpStatusCode.NotFound) {
          this.showInvalidInvitation();
          return;
        }

        // The invited user is already ACTIVE: the account exists, so sign in.
        if (error.status === HttpStatusCode.Conflict) {
          this.showInvalidInvitation('used');
          return;
        }

        this.errorStore.resolution.set({
          strategy: 'reload',
          url: this.router.url,
        });

        this.router.navigate(['/error']);
      },
    });
  }
}
